import type { ChildAppConfig } from '@shared/app-config'
import type { Emitter } from 'nanoevents'
import type { DevOverlayHandle } from './dev-overlay'
import type { Events } from '@/proxy/shared/events'
import type { LayoutApi } from '@/proxy/shared/layout'
import type { ProxyApiInstance } from '@/proxy/shared/types'
import { classifyLink } from '@/proxy/shared/link-classifier'

const TAG = '[wippy-dev]'

/* eslint-disable no-console, no-alert */
export function createDevHostAPI(
  appConfig: ChildAppConfig,
  emitter: Emitter<Events>,
  overlay: DevOverlayHandle | null,
): ProxyApiInstance['host'] {
  // Standalone dev proxy has no real host, so track the forced theme locally
  // and emulate the host broadcast so getThemeMode() stays consistent with set.
  let currentThemeMode: 'auto' | 'light' | 'dark' = appConfig.themeMode ?? 'auto'
  return {
    bridge: {
      post: (channel, payload) => {
        console.log(TAG, 'bridge.post', channel, payload)
      },
      request: async (channel, payload) => {
        console.log(TAG, 'bridge.request', channel, payload)
        throw new Error('host.bridge.request is unavailable in the standalone dev proxy')
      },
      on: (channel, _handler) => {
        console.log(TAG, 'bridge.on', channel)
        return () => {}
      },
    },
    toast: (message) => {
      console.log(TAG, 'toast', message)
    },
    confirm: (options) => {
      const result = window.confirm(options.message || 'Confirm?')
      return Promise.resolve(result)
    },
    startChat: (start_token, options) => {
      console.log(TAG, 'startChat', start_token, options)
    },
    openSession: (sessionUUID, options) => {
      console.log(TAG, 'openSession', sessionUUID, options)
    },
    openArtifact: (artifactUUID, options) => {
      console.log(TAG, 'openArtifact', artifactUUID, options)
    },
    navigate: (url) => {
      console.log(TAG, 'navigate →', url)
      // Emulate navigation: fire @history so child router picks it up
      emitter.emit('@history', { path: url })
      overlay?.updatePath(url)
    },
    onRouteChanged: (internalRoute) => {
      console.log(TAG, 'onRouteChanged', internalRoute)
      overlay?.updatePath(internalRoute)
    },
    handleError: (code, error) => {
      console.error(TAG, 'handleError', code, error)
    },
    setContext: (context, sessionUUID, source) => {
      console.log(TAG, 'setContext', { context, sessionUUID, source })
    },
    formatUrl: (relativeUrl) => {
      return `${appConfig.routePrefix || ''}${relativeUrl}`
    },
    classifyLink: (href) => {
      return classifyLink(href, {
        mountRoutes: appConfig.mountRoutes,
        routePrefix: appConfig.routePrefix,
      })
    },
    // Annotated with `satisfies LayoutApi` so adding a new method on
    // the interface produces a TypeScript error here — without the
    // annotation, an inline literal with extra `console.log` wrappers
    // could silently miss new methods. Cannot use `createNoopLayoutApi`
    // directly because this stub also logs each call to the dev
    // overlay; we keep the wrappers but pin the shape via `satisfies`.
    layout: ({
      get snapshot() {
        return null
      },
      resizePanel: (panelId, size) => console.log(TAG, 'layout.resizePanel', panelId, size),
      collapsePanel: panelId => console.log(TAG, 'layout.collapsePanel', panelId),
      expandPanel: panelId => console.log(TAG, 'layout.expandPanel', panelId),
      openDrawer: panelId => console.log(TAG, 'layout.openDrawer', panelId),
      closeDrawer: panelId => console.log(TAG, 'layout.closeDrawer', panelId),
      toggleDrawer: panelId => console.log(TAG, 'layout.toggleDrawer', panelId),
      movePanel: (panelId, target) => console.log(TAG, 'layout.movePanel', panelId, target),
      removePanel: panelId => console.log(TAG, 'layout.removePanel', panelId),
      updatePanel: (panelId, def) => console.log(TAG, 'layout.updatePanel', panelId, def),
      openModal: (id, def) => console.log(TAG, 'layout.openModal', id, def),
      closeModal: modalId => console.log(TAG, 'layout.closeModal', modalId),
      addFloating: (id, def) => console.log(TAG, 'layout.addFloating', id, def),
      removeFloating: floatingId => console.log(TAG, 'layout.removeFloating', floatingId),
      broadcast: (channel, payload) => console.log(TAG, 'layout.broadcast', channel, payload),
      send: (target, channel, payload) => console.log(TAG, 'layout.send', target, channel, payload),
      on: (channel, _handler) => {
        console.log(TAG, 'layout.on', channel)
        return () => {}
      },
    } satisfies LayoutApi),
    logout: () => {
      console.log(TAG, 'logout')
    },
    setThemeMode: (mode) => {
      console.log(TAG, 'setThemeMode', mode)
      currentThemeMode = mode
      // Emulate the host broadcast so the standalone dev proxy applies it.
      emitter.emit('@theme', mode)
    },
    getThemeMode: () => currentThemeMode,
  }
}
/* eslint-enable no-console, no-alert */
